import React, {
   useState,
   useEffect,
   useRef,
   useLayoutEffect,
   useMemo,
} from "react";
import "./ECLTable.css";
import useSelectorId from "../../../utilities/hooks/useSelectorId";
import { ECLCheckBox } from "../../index";
import { scrollSyncHorizontal } from "../../../utilities/scrollSync";
import { FixedSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import ECLHeaderCell from "./components/ECLHeaderCell";
import RightColumns from "./components/RightColumns";
import LeftColumns from "./components/LeftColumns";
import BodyColumns from "./components/BodyColumns";

/**
 * ECLTable expects a loader function be provided, which defines the following objects
 *  - columnSettings - array of column definition objects with the follwing properties
 *          -label [REQUIRED]: the header string for the column
 *          -width [REQUIRED]: width in pixels of the column
 *          -id [REQUIRED]: the id of the column itself (To refer to ordering/selection/ect.)
 *          -alignment: left/right/center, defaults 'left'
 *          -textWrap: if true, text boxes word wrap.  Default is ellipsis truncation
 *          -fontWeight: "italic", "normal", "bold", defaults to normal
 *          -customFormatting:  takes a function that will be provided row index and cell value, can return whatever JSX/HTML you want to put in there.
 *              Ignores textWrap and fontWeight, is placed in .ecl-table-cell, which is a flex container.  alignment corresponds to justify-content: "flex-start"/"center"/"flex-end"
 *          -additionalRows: Adds extra rows to column header (Must be consistent across all columns, or at least, no column can have more rows than the first column)
 *  - rowData - array of arrays containing the display string -or- a relevant value.
 *  - tableSettings - Object of settings for the table itself
 *      -tableHeight - sets height of table
 *      -columnOrder:  If not set, columns come in whatever order REST prefers
 *      -leftStickyCols: array of ids to be placed in the left sticky column
 *      -rightStickyCols: array of ids to be placed in the right sticky column
 *      -columnHide: array of ids of columns to be hidden
 *      -rowHeight: height of row of content (not variable)
 *      -selectAction: callback that passes rowID and state of the checkbox into a function
 *      -selectAllAction: callback that passes state of the select all button, if defined adds a select box to the select column header for select all functionality.
 *      -rowActions: array of objects that map in row buttons, must have:
 *           -label: label of the button
 *           -action: this is the associated function ID held on the server, that we pass back along with the chosen rowID
 *      -stickyRowActions: if true, adds the row actions to the right sticky column, if false adds them to them at the end of the body columns (if no
 *          rowActions, this is ignored)
 *      -stickySelections: if True, selection checkboxs are placed in the left sticky column, if false, they are placed before the rest of the body columns
 *       -hoverIndicator: adds highlight styling to row items in column on hover
 *       -noVirtualization: removes virtualization for table
 *
 * @export
 * @param {Object} props
 * @return {ReactComponent}
 */

export default function ECLTable(props) {
   const instanceId = useSelectorId();
   const [reactWindowHack, setReactWindowHack] = useState(true);
   //Ugh...  Necessary to hack into react-window
   const tableBodyContainer = useRef(null);
   const tableBodyContainerInner = useRef(null);
   const stickyLeftContainer = useRef(null);
   const stickyRightContainer = useRef(null);
   const tableContainer = useRef(null);
   //Scrolllockers
   const scrollLockLeft = useRef(false);
   const scrollLockBody = useRef(false);
   const scrollLockRight = useRef(false);

   useLayoutEffect(() => {
      setTimeout(() => {
         setReactWindowHack(false);
      }, 100);
   }, []);

   //React-window Hack
   useEffect(() => {
      let syncer = document.querySelector("#ecl-table-header" + instanceId);
      let tableBody = tableBodyContainer.current;
      if (!tableBody) {
         let dialogs = document.querySelectorAll("dialog");
         let thisDialog = Array.from(dialogs).find((el) =>
            el.querySelector("#ecl-table-header" + instanceId),
         );
         if (thisDialog) {
            thisDialog.addEventListener("toggle", () => {
               if (thisDialog.open) {
                  setReactWindowHack(!reactWindowHack);
               }
            });
         }
      }
      if (tableBody && syncer) {
         tableBody.addEventListener(
            "scroll",
            (e) => {
               syncer.scrollLeft = tableBody.scrollLeft;
               props.scrollHandler && props.scrollHandler(e);
            },
            false,
         );
         syncer.addEventListener("scroll", () => {
            tableBody.scrollLeft = syncer.scrollLeft;
         });
      }

      if (props.tableSettings.noVirtualization) {
         if (stickyRightContainer.current) {
            stickyRightContainer.current.addEventListener("scroll", () => {
               if (scrollLockRight.current) {
                  scrollLockRight.current = false;
                  return null;
               }
               const current = stickyRightContainer.current.scrollTop;
               scrollLockLeft.current = true;
               scrollLockBody.current = true;
               if (tableBodyContainer.current) {
                  tableBodyContainer.current.scrollTop = current;
               }
               if (stickyLeftContainer.current) {
                  stickyLeftContainer.current.scrollTop = current;
               }
            });
         }
      }
   }, [reactWindowHack]);

   const getVisibleRows = useMemo(() => {
      return props.rowData.filter((r) => !r.hide);
   }, [props.rowData]);

   //Need to derive stickyColumn widths
   let leftStickyColumnWidth = 0;
   if (
      props.rowData?.[0]?.selected !== undefined &&
      props.rowData?.[0]?.selected !== null &&
      props.tableSettings.stickySelections
   ) {
      //Select boxes are 45px
      leftStickyColumnWidth = leftStickyColumnWidth + 45;
   }
   props.tableSettings.leftStickyCols &&
      props.tableSettings.leftStickyCols.forEach((key) => {
         if (!props.tableSettings.columnHide?.includes(key)) {
            if (props.columnSettings.find((c) => c.id === key).width) {
               leftStickyColumnWidth =
                  leftStickyColumnWidth +
                  props.columnSettings.find((c) => c.id === key).width;
            }
         }
      });

   let rightStickyColumnWidth = 0;

   if (
      (props.tableSettings.rowActions &&
         props.tableSettings.stickyRowActions) ||
      props.tableSettings.rightStickyCols
   ) {
      rightStickyColumnWidth = rightStickyColumnWidth + 6; //border, scrollbar
   }

   if (props.tableSettings.rowActions && props.tableSettings.stickyRowActions) {
      //row actions are always 45 px
      rightStickyColumnWidth = rightStickyColumnWidth + 45;
   }

   if (props.tableSettings.rightStickyCols) {
      props.tableSettings.rightStickyCols.forEach((key) => {
         if (!props.tableSettings.columnHide?.includes(key)) {
            if (props.columnSettings.find((c) => c.id === key).width) {
               rightStickyColumnWidth =
                  rightStickyColumnWidth +
                  props.columnSettings.find((c) => c.id === key).width;
            }
         }
      });
   }

   //Table doesn't flex, once it grows to max size it's done.
   //start with border and scrollbar
   let tableMaxWidth = 2 + 5;
   if (rightStickyColumnWidth) {
      tableMaxWidth = tableMaxWidth + 1 + rightStickyColumnWidth;
   }

   if (leftStickyColumnWidth) {
      tableMaxWidth = tableMaxWidth + 1 + leftStickyColumnWidth;
   }

   props.tableSettings.columnOrder.forEach((key) => {
      if (!props.tableSettings.columnHide?.includes(key)) {
         let columnInfo = props.columnSettings.find((c) => c.id === key);
         if (!columnInfo) {
            console.error("Column not found:", key);
         }
         tableMaxWidth = tableMaxWidth + (columnInfo ? columnInfo.width : 150);
      }
   });

   //If row actions aren't sticky, add to table max width
   if (
      props.tableSettings.rowActions &&
      !props.tableSettings.stickyRowActions
   ) {
      //row actions are always 45 px
      tableMaxWidth = tableMaxWidth + 45;
   }

   //if selections aren't sticky, add to table max width
   if (
      props.rowData?.[0]?.selected !== undefined &&
      props.rowData?.[0]?.selected !== null &&
      !props.tableSettings.stickySelections
   ) {
      //Select boxes are 45px
      tableMaxWidth = tableMaxWidth + 45;
   }

   return (
      <div
         role="table"
         id={"table-container-" + instanceId}
         ref={tableContainer}
         aria-rowcount={props.rowData.length}
         className="ecl-table-container table-component-table"
         //added 5px to height here so that the vertical scroll bar doesn't show up when the horizontal scrollbar does
         style={{
            maxWidth: tableMaxWidth + "px",
            height: props.tableSettings.tableHeight
               ? props.tableSettings.tableHeight + 5 + "px"
               : "100%",
         }}
      >
         <div
            className="ecl-table-header-container"
            style={{
               height: props.columnSettings[0].additionalRows
                  ? props.columnSettings[0].additionalRows.length * 40 + "px"
                  : "40px",
            }}
         >
            {leftStickyColumnWidth > 0 && (
               <div
                  className="ecl-table-header-sticky-left"
                  role="row"
                  style={{
                     width: leftStickyColumnWidth + "px",
                     minWidth: leftStickyColumnWidth + "px",
                     display: "flex",
                  }}
               >
                  {props.rowData?.[0]?.selected !== undefined &&
                     props.tableSettings.stickySelections && (
                        <div
                           role="columnheader"
                           className="ecl-table-cell header-cell"
                           style={{
                              width: "45px",
                              minWidth: "45px",
                              display: "flex",
                              justifyContent: "center",
                           }}
                        >
                           {props.tableSettings.selectAllAction !==
                              undefined && (
                              <ECLCheckBox
                                 id={"table-select-all-" + instanceId}
                                 onClick={(e) => {
                                    props.tableSettings.selectAllAction(
                                       e.target.checked,
                                    );
                                 }}
                              />
                           )}
                        </div>
                     )}
                  {props.tableSettings.leftStickyCols &&
                     props.tableSettings.leftStickyCols.map((key) => {
                        return (
                           <ECLHeaderCell
                              columnInfo={props.columnSettings.find(
                                 (c) => c.id === key,
                              )}
                              columnSettings={props.columnSettings}
                              hide={
                                 props.tableSettings.columnHide
                                    ? props.tableSettings.columnHide.includes(
                                         key,
                                      )
                                    : false
                              }
                              key={key}
                              containerId={"table-container-" + instanceId}
                           />
                        );
                     })}
               </div>
            )}
            <div
               id={"ecl-table-header" + instanceId}
               role="row"
               className="ecl-table-row header-row"
               style={{
                  width:
                     "calc(100% - " +
                     (rightStickyColumnWidth + leftStickyColumnWidth) +
                     "px)",
                  paddingRight: "5px",
               }}
               onScroll={(e) => {
                  e.preventDefault();
                  scrollSyncHorizontal(
                     "ecl-table-header" + instanceId,
                     "ecl-table-body" + instanceId,
                  );
               }}
            >
               {props.rowData?.[0]?.selected !== undefined &&
                  !props.tableSettings.stickySelections && (
                     <div
                        role="columnheader"
                        className="ecl-table-cell header-cell"
                        style={{
                           width: "45px",
                           minWidth: "45px",
                           display: "flex",
                           justifyContent: "center",
                        }}
                     >
                        {props.tableSettings.selectAllAction !== undefined && (
                           <ECLCheckBox
                              id={"table-select-all-" + instanceId}
                              onClick={(e) => {
                                 props.tableSettings.selectAllAction(
                                    e.target.checked,
                                 );
                              }}
                           />
                        )}
                     </div>
                  )}
               {props.tableSettings.columnOrder.map((key) => {
                  return (
                     <ECLHeaderCell
                        columnInfo={props.columnSettings.find(
                           (c) => c.id === key,
                        )}
                        columnSettings={props.columnSettings}
                        hide={
                           props.tableSettings.columnHide
                              ? props.tableSettings.columnHide.includes(key)
                              : false
                        }
                        key={key}
                        containerId={"table-container-" + instanceId}
                     />
                  );
               })}
               {props.tableSettings.rowActions &&
                  !props.tableSettings.stickyRowActions && (
                     <div
                        role="columnheader"
                        className="ecl-table-cell header-cell"
                        style={{ width: "51px", minWidth: "51px" }}
                     ></div>
                  )}
            </div>
            {rightStickyColumnWidth > 0 && (
               <div
                  className="ecl-table-header-sticky-right"
                  role="row"
                  style={{
                     width: rightStickyColumnWidth + "px",
                     minWidth: rightStickyColumnWidth + "px",
                     display: "flex",
                  }}
               >
                  {props.tableSettings.rightStickyCols &&
                     props.tableSettings.rightStickyCols.map((key) => {
                        return (
                           <ECLHeaderCell
                              columnInfo={props.columnSettings.find(
                                 (c) => c.id === key,
                              )}
                              columnSettings={props.columnSettings}
                              hide={
                                 props.tableSettings.columnHide
                                    ? props.tableSettings.columnHide.includes(
                                         key,
                                      )
                                    : false
                              }
                              key={key}
                              containerId={"table-container-" + instanceId}
                           />
                        );
                     })}
                  {props.tableSettings.rowActions &&
                     props.tableSettings.stickyRowActions && (
                        <div
                           role="columnheader"
                           className="ecl-table-cell header-cell"
                           style={{ width: "51px", minWidth: "51px" }}
                        ></div>
                     )}
               </div>
            )}
         </div>
         <div
            className="ecl-table-body-container"
            style={{
               height: props.columnSettings[0].additionalRows
                  ? "calc(100% - " +
                    props.columnSettings[0].additionalRows.length * 40 +
                    "px)"
                  : "calc(100% - 40px)",
               minHeight: props.columnSettings[0].additionalRows
                  ? "calc(100% - " +
                    props.columnSettings[0].additionalRows.length * 40 +
                    "px)"
                  : "calc(100% - 40px)",
            }}
         >
            {props.rowData.length === 0 ? (
               <div className="ecl-table-body-empty">
                  <p>No results to display</p>
               </div>
            ) : (
               <>
                  {leftStickyColumnWidth > 0 && (
                     <div
                        style={{
                           width: leftStickyColumnWidth + "px",
                           minWidth: leftStickyColumnWidth + "px",
                           height: "100%",
                        }}
                     >
                        {props.tableSettings.noVirtualization ? (
                           <>
                              {props.rowData.map((row, index) => {
                                 return (
                                    <RightColumns
                                       row={row}
                                       index={index}
                                       tableSettings={props.tableSettings}
                                       columnSettings={props.columnSettings}
                                       instanceId={instanceId}
                                       selection={
                                          props.rowData?.[0]?.selected !==
                                          undefined
                                       }
                                       key={index}
                                    />
                                 );
                              })}
                           </>
                        ) : (
                           <AutoSizer>
                              {({ height, width }) => {
                                 return (
                                    <FixedSizeList
                                       height={height}
                                       itemCount={getVisibleRows.length}
                                       itemSize={props.tableSettings.rowHeight}
                                       width={width}
                                       outerRef={stickyLeftContainer}
                                       className="ecl-table-body-sticky sticky-col-left"
                                       onScroll={() => {
                                          if (scrollLockLeft.current) {
                                             scrollLockLeft.current = false;
                                             return null;
                                          }
                                          const current =
                                             stickyLeftContainer.current
                                                .scrollTop;
                                          scrollLockBody.current = true;
                                          scrollLockRight.current = true;

                                          if (tableBodyContainer.current) {
                                             tableBodyContainer.current.scrollTop =
                                                current;
                                          }
                                          if (stickyRightContainer.current) {
                                             stickyRightContainer.current.scrollTop =
                                                current;
                                          }
                                       }}
                                    >
                                       {({ index, style }) => (
                                          <LeftColumns
                                             row={getVisibleRows[index]}
                                             index={index}
                                             tableSettings={props.tableSettings}
                                             columnSettings={
                                                props.columnSettings
                                             }
                                             instanceId={instanceId}
                                             style={style}
                                             selection={
                                                props.rowData?.[0]?.selected !==
                                                undefined
                                             }
                                          />
                                       )}
                                    </FixedSizeList>
                                 );
                              }}
                           </AutoSizer>
                        )}
                     </div>
                  )}
                  <div
                     style={{
                        width:
                           "calc(100% - " +
                           (rightStickyColumnWidth + leftStickyColumnWidth) +
                           "px)",
                        minHeight: "110px", //header is 40 px, min height of a table is 110px
                        height: "100%",
                        overflow: props.tableSettings.noVirtualization
                           ? "auto"
                           : "hidden",
                     }}
                  >
                     {props.tableSettings.noVirtualization ? (
                        <div
                           className={
                              "ecl-table-body " +
                              (rightStickyColumnWidth > 0
                                 ? "nested-table-body"
                                 : "")
                           }
                           onScroll={() => {
                              if (scrollLockBody.current) {
                                 scrollLockBody.current = false;
                                 return null;
                              }
                              const current =
                                 tableBodyContainer.current.scrollTop;
                              scrollLockLeft.current = true;
                              scrollLockRight.current = true;

                              if (stickyRightContainer.current) {
                                 stickyRightContainer.current.scrollTop =
                                    current;
                              }
                              if (stickyLeftContainer.current) {
                                 stickyLeftContainer.current.scrollTop =
                                    current;
                              }
                           }}
                           ref={tableBodyContainer}
                        >
                           {props.rowData.map((row, index) => {
                              return (
                                 <BodyColumns
                                    row={row}
                                    index={index}
                                    tableSettings={props.tableSettings}
                                    columnSettings={props.columnSettings}
                                    instanceId={instanceId}
                                    selection={
                                       props.rowData?.[0]?.selected !==
                                       undefined
                                    }
                                    key={index}
                                 />
                              );
                           })}
                        </div>
                     ) : (
                        <AutoSizer>
                           {({ height, width }) => {
                              return (
                                 <FixedSizeList
                                    className={
                                       "ecl-table-body " +
                                       (rightStickyColumnWidth > 0
                                          ? "nested-table-body"
                                          : "")
                                    }
                                    height={height}
                                    itemCount={getVisibleRows.length}
                                    itemSize={props.tableSettings.rowHeight}
                                    width={width}
                                    outerRef={tableBodyContainer}
                                    innerRef={tableBodyContainerInner}
                                    onScroll={() => {
                                       if (scrollLockBody.current) {
                                          scrollLockBody.current = false;
                                          return null;
                                       }
                                       const current =
                                          tableBodyContainer.current.scrollTop;
                                       scrollLockLeft.current = true;
                                       scrollLockRight.current = true;

                                       if (stickyRightContainer.current) {
                                          stickyRightContainer.current.scrollTop =
                                             current;
                                       }
                                       if (stickyLeftContainer.current) {
                                          stickyLeftContainer.current.scrollTop =
                                             current;
                                       }
                                    }}
                                 >
                                    {({ index, style }) => (
                                       <BodyColumns
                                          row={getVisibleRows[index]}
                                          index={index}
                                          tableSettings={props.tableSettings}
                                          columnSettings={props.columnSettings}
                                          instanceId={instanceId}
                                          selection={
                                             props.rowData?.[0]?.selected !==
                                             undefined
                                          }
                                          key={index}
                                          style={style}
                                       />
                                    )}
                                 </FixedSizeList>
                              );
                           }}
                        </AutoSizer>
                     )}
                  </div>
                  {rightStickyColumnWidth > 0 && (
                     <div
                        style={{
                           width: rightStickyColumnWidth + "px",
                           minWidth: rightStickyColumnWidth + "px",
                           height: "100%",
                           overflow: props.tableSettings.noVirtualization
                              ? "auto"
                              : "hidden",
                        }}
                        id={"right-sticky-table-div" + instanceId}
                        ref={
                           props.tableSettings.noVirtualization &&
                           stickyRightContainer
                        }
                     >
                        {props.tableSettings.noVirtualization ? (
                           <>
                              {props.rowData.map((row, index) => {
                                 return (
                                    <RightColumns
                                       row={row}
                                       index={index}
                                       tableSettings={props.tableSettings}
                                       columnSettings={props.columnSettings}
                                       instanceId={instanceId}
                                       key={index}
                                    />
                                 );
                              })}
                           </>
                        ) : (
                           <AutoSizer>
                              {({ height, width }) => {
                                 return (
                                    <FixedSizeList
                                       height={height}
                                       className="ecl-table-body-sticky sticky-col-right"
                                       itemCount={getVisibleRows.length}
                                       itemSize={props.tableSettings.rowHeight}
                                       width={width}
                                       outerRef={stickyRightContainer}
                                       onScroll={() => {
                                          if (scrollLockRight.current) {
                                             scrollLockRight.current = false;
                                             return null;
                                          }
                                          const current =
                                             stickyRightContainer.current
                                                .scrollTop;
                                          scrollLockLeft.current = true;
                                          scrollLockBody.current = true;
                                          if (tableBodyContainer.current) {
                                             tableBodyContainer.current.scrollTop =
                                                current;
                                          }
                                          if (stickyLeftContainer.current) {
                                             stickyLeftContainer.current.scrollTop =
                                                current;
                                          }
                                       }}
                                    >
                                       {({ index, style }) => (
                                          <RightColumns
                                             row={getVisibleRows[index]}
                                             index={index}
                                             tableSettings={props.tableSettings}
                                             columnSettings={
                                                props.columnSettings
                                             }
                                             instanceId={instanceId}
                                             style={style}
                                          />
                                       )}
                                    </FixedSizeList>
                                 );
                              }}
                           </AutoSizer>
                        )}
                     </div>
                  )}
               </>
            )}
         </div>
      </div>
   );
}
